import React from "react";

import { cva, VariantProps } from "class-variance-authority";
import { cn } from "src/helpers";
import { Slot } from "@radix-ui/react-slot";
import PulseLoader from "./PulseLoader";

const buttonVariants = cva(
  " rounded-sm inline-flex items-center justify-center whitespace-nowrap  text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary hover:text-white",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "border border-primary bg-white text-primary shadow-sm hover:bg-primary hover:text-white",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const RoundedButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <>
        {isLoading ? (
          <Comp
            disabled={isLoading || false}
            className={cn("md:min-w-[150px] h-9 ", buttonVariants({ variant, size, className }))}
            ref={ref}
            {...props}
          >
            <PulseLoader />{" "}
          </Comp>
        ) : (
          <Comp
            disabled={isLoading || false}
            className={cn("md:min-w-[150px] h-9 ", buttonVariants({ variant, size, className }))}
            ref={ref}
            {...props}
          />
        )}
      </>
    );
  }
);

export default RoundedButton;
